import React, { useEffect, useState } from "react";
import { Monitor } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { desk, setDeskMode } from "@/pages/admin/desktop/deskApi";
import { useDesktopBrand } from "./useDesktopBrand";
import DesktopTab from "@/pages/admin/desktop/DesktopTab";
import InstallAppButton from "@/components/InstallAppButton";
import { DesktopGoogleButton, startDesktopGoogle } from "./DesktopGoogleButton";

export default function DesktopPortalPage() {
  useDesktopBrand();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(null); // null=checking, false=login, obj=in
  const isAdmin = ["admin", "mgmt"].includes(user?.role) && !user?.is_demo;
  const adminDesk = isAdmin && (location.state?.admin || !localStorage.getItem("desktop_token"));
  setDeskMode(adminDesk ? "admin" : null);

  useEffect(() => {
    if (loading || adminDesk) return;
    if (!localStorage.getItem("desktop_token")) { setMe(false); return; }
    desk.get("/desktop/auth/me")
      .then((r) => setMe(r.data))
      .catch(() => { localStorage.removeItem("desktop_token"); setMe(false); });
  }, [loading, adminDesk]);

  const signOut = () => {
    localStorage.removeItem("desktop_token");
    setMe(false);
  };

  if (adminDesk) return <DesktopTab onExit={() => nav("/managementusa/admin", { replace: true })} />;
  if (me === null) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400 text-sm">Loading…</div>;
  }
  if (me === false) return <DeskLogin onDone={setMe} />;
  return <DesktopTab deskUser={me} onExit={signOut} />;
}

function DeskLogin({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await desk.post("/desktop/auth/login", { email, password });
      localStorage.setItem("desktop_token", data.token);
      onDone(data.user);
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "radial-gradient(120% 90% at 20% 10%, #2563eb 0%, #1e3a8a 38%, #0f172a 100%)" }}>
      <form onSubmit={submit} className="w-full max-w-sm bg-white/95 backdrop-blur rounded-xl shadow-2xl p-6 space-y-4" data-testid="desk-login-form">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
            <Monitor className="w-5 h-5 text-sky-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Desktop sign-in</h1>
            <p className="text-xs text-slate-500">Your own workspace — documents &amp; e-sign</p>
          </div>
        </div>
        {err && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="desk-login-error">{err}</p>}
        <DesktopGoogleButton onClick={() => startDesktopGoogle()} disabled={busy} testId="desk-google-login" />
        <div className="flex items-center gap-3" aria-hidden="true"><span className="h-px flex-1 bg-slate-200" /><span className="text-[11px] font-semibold uppercase text-slate-400">or use password</span><span className="h-px flex-1 bg-slate-200" /></div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="desk-login-email" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Password</label>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="desk-login-password" />
        </div>
        <button type="submit" disabled={busy}
          className="w-full bg-sky-600 hover:bg-sky-700 text-white font-bold text-sm rounded-md py-2.5 disabled:opacity-60"
          data-testid="desk-login-submit">
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <div className="flex justify-center pt-1">
          <InstallAppButton manifest="/manifest-desktop.json" appleIcon="/desk-apple-touch.png" appName="the Management USA NJ desktop" allowDesktop className="h-9 w-full justify-center" />
        </div>
      </form>
    </div>
  );
}
